import dayjs from 'dayjs'
import { XIcon } from 'lucide-react'

import { VisaBulletinStatusText } from '@/pages/ui/visa-bulletin-status-text/visa-bulletin-status-text'

import { Button } from '@/components/button/button'
import { Card, CardContent } from '@/components/card/card'
import { Separator } from '@/components/separator/separator'

import { type VisaBulletinInformation } from '@/lib/queries/business/types'
import { type ResGetCaseExtraInfoQuery } from '@/lib/queries/business/v3/case-extra/types'
import { cn } from '@/lib/utils'
import { getLocalDate } from '@/lib/utils/datetime'
import { toVisaBulletinDate } from '@/lib/utils/visa-bulletin'

import { FileI485 } from '../file-i-485'
import { VisaBulletinGuideText } from '../visa-bulletin-guide-text'
import { VisaBulletinInfoBox } from '../visa-bulletin-info-box'
import { useVisaBulletinCard } from './use-visa-bulletin-card'
import { VisaBulletinDataDeleteConfirmDialog } from './visa-bulletin-data-delete-confirm-dialog'

export interface VisaBulletinCardProps {
  bulletinDate: VisaBulletinInformation['bulletinDate']
  priorityDate: ResGetCaseExtraInfoQuery['priorityDate']
  finalActionDate: VisaBulletinInformation['finalActionDate']
  filingDate: VisaBulletinInformation['filingDate']
  noticeText: VisaBulletinInformation['noticeText']
  onClickUpdateCaseInfoButton: VoidFunction
  onClickMoreInfoButton: VoidFunction
}

export const VisaBulletinCard = ({
  bulletinDate,
  priorityDate,
  finalActionDate,
  filingDate,
  noticeText,
  onClickUpdateCaseInfoButton,
  onClickMoreInfoButton,
}: VisaBulletinCardProps) => {
  const {
    visaBulletinData,
    isI485Filed,
    isDeletingVisaBulletinDAta,
    isNVCAssociatedCase,
    isOpenVisaBulletinDataDeleteConfirmDialog,
    openVisaBulletinDataDeleteConfirmDialog,
    closeVisaBulletinDataDeleteConfirmDialog,
    handleClickDeleteConfirmButton,
  } = useVisaBulletinCard()

  return (
    <>
      <Card className={cn('min-w-fit')}>
        <CardContent className={cn('px-4 py-6')}>
          <div className={cn('flex items-center justify-between', 'mb-2.5')}>
            <VisaBulletinStatusText
              iconType={visaBulletinData?.current?.bulletinIcon}
              text={visaBulletinData?.current?.bulletinText}
            />
            <Button type="button" variant="ghost" size="icon" onClick={openVisaBulletinDataDeleteConfirmDialog}>
              <XIcon size={16} />
            </Button>
          </div>
          <VisaBulletinInfoBox
            bulletinDate={bulletinDate}
            priorityDate={dayjs(getLocalDate(priorityDate))}
            finalActionDate={toVisaBulletinDate(finalActionDate)}
            filingDate={toVisaBulletinDate(filingDate)}
          />
          <Separator className={cn('my-2.5')} />
          {!isNVCAssociatedCase && <VisaBulletinGuideText text={noticeText} />}
          <div className={cn('flex flex-col space-y-3', 'w-full')}>
            <Button
              variant="outline"
              size="sm"
              className={cn('text-xs font-medium')}
              onClick={onClickUpdateCaseInfoButton}
            >
              Update Case Info
            </Button>
            <Button variant="outline" size="sm" className={cn('text-xs font-medium')} onClick={onClickMoreInfoButton}>
              Visa Bulletin Trends
            </Button>
          </div>
          {!isNVCAssociatedCase && <FileI485 className="mt-2.5" filed={isI485Filed} />}
        </CardContent>
      </Card>

      <VisaBulletinDataDeleteConfirmDialog
        isOpen={isOpenVisaBulletinDataDeleteConfirmDialog}
        isDeleting={isDeletingVisaBulletinDAta}
        close={closeVisaBulletinDataDeleteConfirmDialog}
        onClickConfirmButton={handleClickDeleteConfirmButton}
      />
    </>
  )
}
